import React from "react";
import { BookOpen, ChevronRight } from "lucide-react";
import { phaseForDate, computeIntake } from "../lib/cycle";
import { useI18n } from "../i18n";
import PhaseArticle from "./PhaseArticle";
import InfoArticle from "./InfoArticle";
import { METRIC_ICONS, directionMeta, focusMeta, LevelDots, Droplet, Beef } from "./metricUtils";

const METRIC_KEY_MAP = { activity: "movement", carbs: "carbs", fat: "fats" };

function MetricCard({ metricKey, metric, t }) {
  const Icon = METRIC_ICONS[metricKey];
  const dir = directionMeta(metric.direction);
  return (
    <div data-testid={`rec-${metricKey}`} className="bg-bone border border-border rounded-2xl p-4 flex gap-3">
      <div className="h-10 w-10 shrink-0 rounded-xl grid place-items-center" style={{ backgroundColor: dir.bg }}>
        <Icon className="h-5 w-5" style={{ color: dir.color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground">
            {t(METRIC_KEY_MAP[metricKey])}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ color: dir.color, backgroundColor: dir.bg }}>
            <dir.Icon className="h-3 w-3" />
            {t(metric.direction)}
          </span>
        </div>
        <div className="mt-1.5 mb-2">
          <LevelDots level={metric.level} color={dir.color} testid={`level-${metricKey}`} />
        </div>
        <p className="text-xs text-muted-foreground leading-snug">{metric.note}</p>
        {metric.alt && (
          <div data-testid={`rec-${metricKey}-alt`} className="mt-2 pt-2 border-t border-border flex items-start gap-1.5">
            {(() => {
              const altDir = directionMeta(metric.alt.direction);
              return (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0" style={{ color: altDir.color, backgroundColor: altDir.bg }}>
                  <altDir.Icon className="h-2.5 w-2.5" />
                  {t(metric.alt.direction)}
                </span>
              );
            })()}
            <p className="text-[11px] text-muted-foreground leading-snug">{metric.alt.note}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function IntakeCard({ icon: Icon, label, value, unit, sub, color, testid, footer }) {
  return (
    <div data-testid={testid} className="bg-bone border border-border rounded-2xl p-4 flex items-center gap-3 h-full">
      <div className="h-11 w-11 shrink-0 rounded-xl grid place-items-center" style={{ backgroundColor: color + "22" }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground">{label}</div>
        <div className="font-serif text-2xl leading-none mt-1">
          {value}
          <span className="text-sm font-sans text-muted-foreground ml-1">{unit}</span>
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
        {footer && (
          <div className="text-[11px] font-bold mt-1 inline-flex items-center gap-0.5" style={{ color }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PhaseRecommendations({ data, calendar }) {
  const { phases, water, protein, t } = useI18n();
  const today = new Date();
  const { phaseKey, cycleDay } = phaseForDate(today, data);
  const phase = phases[phaseKey];
  const intake = computeIntake(data.weight, phase.proteinFactor);
  const fMeta = focusMeta(phase.focus);
  const footer = (
    <>
      <BookOpen className="h-3 w-3" /> {t("whyThisMatters")} <ChevronRight className="h-3 w-3" />
    </>
  );

  return (
    <div className="space-y-4 animate-fade-up">
      <PhaseArticle phase={phase}>
        <div
          data-testid="current-phase-banner"
          role="button"
          tabIndex={0}
          className="rounded-3xl p-6 relative overflow-hidden cursor-pointer transition-transform hover:-translate-y-0.5 active:scale-[0.99]"
          style={{ backgroundColor: phase.color, color: phase.textColor }}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[11px] tracking-[0.25em] uppercase font-bold opacity-80">
                {t("dayToday", { n: cycleDay })}
              </div>
              <h2 className="font-serif text-4xl leading-none mt-2">{phase.label}</h2>
              <div className="text-sm font-semibold mt-1 opacity-90">{phase.short}</div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full backdrop-blur" style={{ backgroundColor: "rgba(255,255,255,0.18)" }}>
              <fMeta.Icon className="h-3.5 w-3.5" />
              {t(phase.focus === "fitness" ? "fitnessWindow" : "hormoneCare")}
            </span>
          </div>
          <p className="text-sm mt-4 leading-relaxed opacity-95 max-w-md">{phase.hormone}</p>
          <div data-testid="read-article-hint" className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.18)" }}>
            <BookOpen className="h-3.5 w-3.5" />
            {t("readScience")}
            <ChevronRight className="h-3.5 w-3.5" />
          </div>
        </div>
      </PhaseArticle>

      <div className="grid grid-cols-2 gap-3 items-stretch">
        <InfoArticle article={water} color="#3E7CB1" label={t("hydrationLabel")} testid="water-article">
          <div role="button" tabIndex={0} data-testid="water-card-trigger" className="cursor-pointer transition-transform hover:-translate-y-0.5 active:scale-[0.99]">
            <IntakeCard testid="water-intake-card" icon={Droplet} label={t("water")} value={intake.waterOz} unit={t("flOz")} sub={t("cupsPerDay", { n: intake.waterCups })} color="#3E7CB1" footer={footer} />
          </div>
        </InfoArticle>
        <InfoArticle article={protein} color="#9B3926" label={t("nutritionLabel")} testid="protein-article">
          <div role="button" tabIndex={0} data-testid="protein-card-trigger" className="cursor-pointer transition-transform hover:-translate-y-0.5 active:scale-[0.99]">
            <IntakeCard testid="protein-intake-card" icon={Beef} label={t("protein")} value={intake.proteinG} unit={t("grams")} sub={phase.proteinNote} color="#9B3926" footer={footer} />
          </div>
        </InfoArticle>
      </div>

      {calendar}

      <div className="space-y-3">
        <MetricCard metricKey="activity" metric={phase.activity} t={t} />
        <MetricCard metricKey="carbs" metric={phase.carbs} t={t} />
        <MetricCard metricKey="fat" metric={phase.fat} t={t} />
      </div>

      <div data-testid="phase-tips" className="bg-bone border border-border rounded-2xl p-5">
        <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-3">
          {t("focusForPhase")}
        </div>
        <ul className="space-y-2.5">
          {phase.tips.map((tip) => (
            <li key={tip} className="flex gap-2.5 text-sm text-foreground/90">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: phase.color }} />
              {tip}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
